import { Injectable, ComponentFactoryResolver, ApplicationRef, Injector, EmbeddedViewRef, ComponentRef, Type } from '@angular/core';
import { DialogConfig } from './is-dialog-config';
import { ISDialogComponent } from './is-dialog.component';
import { ISDialogTaskbarComponent } from './is-dialog-taskbar.component';
import { DialogRef } from './is-dialog-ref';
import { DialogInjector } from './is-dialog-injector';

@Injectable({ providedIn: 'root' })
export class ISDialogService {
  dialogComponentRefs: ComponentRef<ISDialogComponent>[] = [];
  private taskbarRef: ComponentRef<ISDialogTaskbarComponent> | null = null;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector
  ) {}

  /** Mantém a assinatura atual, não exige alterações aos consumidores. */
  public open(componentType: Type<any>, config: DialogConfig): DialogRef {
    this.ensureTaskbar();
    const dialogRef = this.appendDialogComponentToBody(config);
    const dialogComponentRef = this.dialogComponentRefs[this.dialogComponentRefs.length - 1];
    dialogComponentRef.instance.childComponentType = componentType;
    return dialogRef;
  }

  /** API antiga sem argumentos de identificação: mantida para compatibilidade. */
  public close(data?: any): void {}

  public getDialogRef(predicate: (dialogInstance: ISDialogComponent) => boolean): DialogRef | null {
    const component = this.dialogComponentRefs.find(ref => predicate(ref.instance));
    return component ? component.instance.dialogRef : null;
  }

  public getDialogInstance(predicate: (dialogInstance: ISDialogComponent) => boolean): ISDialogComponent | null {
    const component = this.dialogComponentRefs.find(ref => predicate(ref.instance));
    return component ? component.instance : null;
  }

  private ensureTaskbar(): void {
    if (this.taskbarRef) return;
    const factory = this.componentFactoryResolver.resolveComponentFactory(ISDialogTaskbarComponent);
    const ref = factory.create(this.injector);
    this.appRef.attachView(ref.hostView);
    const root = (ref.hostView as EmbeddedViewRef<unknown>).rootNodes[0] as HTMLElement;
    document.body.appendChild(root);
    this.taskbarRef = ref;
  }

  private appendDialogComponentToBody(config: DialogConfig): DialogRef {
    const tokens = new WeakMap<any, any>();
    tokens.set(DialogConfig, config);
    const dialogRef = new DialogRef();
    tokens.set(DialogRef, dialogRef);

    const factory = this.componentFactoryResolver.resolveComponentFactory(ISDialogComponent);
    const componentRef = factory.create(new DialogInjector(this.injector, tokens));
    // Usamos exclusivamente afterClosed como ponto de destruição. Evita destruir
    // o componente filho antes de getDialogCloseResult() ser consultado.
    const sub = dialogRef.afterClosed.subscribe(() => {
      this.removeDialogComponentFromBody(componentRef);
      sub.unsubscribe();
    });

    this.appRef.attachView(componentRef.hostView);
    const root = (componentRef.hostView as EmbeddedViewRef<unknown>).rootNodes[0] as HTMLElement;
    document.body.appendChild(root);
    this.dialogComponentRefs.push(componentRef);
    return dialogRef;
  }

  private removeDialogComponentFromBody(ref: ComponentRef<ISDialogComponent>): void {
    const index = this.dialogComponentRefs.indexOf(ref);
    if (index < 0) return;
    this.dialogComponentRefs.splice(index, 1);
    this.appRef.detachView(ref.hostView);
    ref.destroy();

    if (this.dialogComponentRefs.length === 0 && this.taskbarRef) {
      this.appRef.detachView(this.taskbarRef.hostView);
      this.taskbarRef.destroy();
      this.taskbarRef = null;
    }
  }
}
